/**
 * Content Management → Content Library (Infographics & Cinegraphics)
 *
 * Review queue for everything a support-staff or admin has uploaded as an
 * infographic or cinegraphic. Same look-and-feel as ContentMgmtSyllabusLibrary
 * so the approval workflow is consistent across asset types. Backed by
 * /api/content/admin-library (all statuses) + /api/content/{id}/approve|reject
 * + DELETE /api/content/{id}.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiPost, apiDelete, apiPatch, API_BASE } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  ShieldCheck, ShieldX, Trash2, RefreshCw, Search, Image as ImageIcon,
  Video, CheckCircle2, AlertTriangle, FileText, Info, ExternalLink, Layers, AlertOctagon,
  Eye, XCircle, Zap, Pencil, X as XIcon, Check as CheckIcon,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_TABS = [
  { key: 'pending_review', label: 'Pending review' },
  { key: 'approved', label: 'Approved' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

const TYPE_TABS = [
  { key: 'all', label: 'All types', icon: Layers },
  { key: 'infographic', label: 'Infographics', icon: ImageIcon },
  { key: 'cinegraphic', label: 'Cinegraphics', icon: Video },
];

const BOARD_FILTER = [
  { value: '', label: 'All boards' },
  { value: 'cbse', label: 'CBSE' },
  { value: 'icse', label: 'ICSE' },
  { value: 'ib', label: 'IB' },
  { value: 'igcse', label: 'Cambridge' },
  { value: 'state_board', label: 'State Board' },
  { value: 'nios', label: 'NIOS' },
];
const CLASS_FILTER = [
  { value: '', label: 'All classes' },
  ...Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: `Class ${i + 1}` })),
];

const FILE_ORIGIN = API_BASE.replace(/\/api$/, '');
const prettyName = (s) => (s || '').replace(/^[a-f0-9]{8}_/i, '');
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatUploadedDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]} ${d.getFullYear()}`;
}

function statusBadge(status) {
  switch (status) {
    case 'approved': return <Badge className="bg-emerald-100 text-emerald-700 rounded-full text-[10px] uppercase tracking-wider">Approved</Badge>;
    case 'rejected': return <Badge className="bg-slate-200 text-slate-600 rounded-full text-[10px] uppercase tracking-wider">Rejected</Badge>;
    default: return <Badge className="bg-amber-100 text-amber-700 rounded-full text-[10px] uppercase tracking-wider">Pending review</Badge>;
  }
}

export default function ContentMgmtContentLibrary({ refreshToken }) {
  const [statusTab, setStatusTab] = useState('pending_review');
  const [typeTab, setTypeTab] = useState('all');
  const [boardFilter, setBoardFilter] = useState('');
  const [classFilter, setClassFilter] = useState('');
  const [subjectFilter, setSubjectFilter] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({ pending_review: 0, approved: 0, rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [mutatingId, setMutatingId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [wiping, setWiping] = useState(false);
  const [visEditingId, setVisEditingId] = useState(null);  // content_id of row whose tier-visibility popover is open
  const [savingVis, setSavingVis] = useState(false);

  // Inline chapter-topic edit state — one row at a time. Enables the
  // admin to "retry match" by renaming a content_library row's topic
  // so it aligns with the canonical syllabus chapter name.
  const [editingTopicId, setEditingTopicId] = useState(null);
  const [editingTopicValue, setEditingTopicValue] = useState('');
  const [savingTopic, setSavingTopic] = useState(false);

  // Aggregate match stats across the CURRENT filtered page — powers the
  // summary bar at the top so an admin uploading hundreds of files can
  // see at a glance: "12 matched · 3 ambiguous · 5 orphaned".
  const matchStats = useMemo(() => {
    let matched = 0, ambiguous = 0, orphaned = 0;
    for (const it of items) {
      const s = it.match_state;
      if (s === 'matched') matched++;
      else if (s === 'ambiguous') ambiguous++;
      else if (s === 'orphaned') orphaned++;
    }
    return { matched, ambiguous, orphaned, total: items.length };
  }, [items]);

  const startEditTopic = (row) => {
    setEditingTopicId(row.content_id);
    setEditingTopicValue(row.topic || '');
  };
  const cancelEditTopic = () => {
    setEditingTopicId(null);
    setEditingTopicValue('');
  };
  const saveTopic = async (row, newTopic) => {
    const trimmed = (newTopic || '').trim();
    if (!trimmed) { toast.error('Chapter name cannot be empty'); return; }
    if (trimmed === (row.topic || '').trim()) { cancelEditTopic(); return; }
    setSavingTopic(true);
    try {
      const res = await apiPatch(`/content/${row.content_id}/topic`, { topic: trimmed });
      // Replace the row in-place with the fresh match state so the badge updates
      setItems(prev => prev.map(x => x.content_id === row.content_id
        ? { ...x, ...(res.content || {}) }
        : x));
      const state = res?.content?.match_state;
      if (state === 'matched') toast.success('Match resolved · students will now see this asset');
      else if (state === 'ambiguous') toast.warning('Still ambiguous — matches multiple chapters');
      else toast.info('Saved. Still orphaned — try the suggested chapter name');
      cancelEditTopic();
    } catch (e) {
      toast.error(e?.message || 'Failed to update chapter');
    }
    setSavingTopic(false);
  };
  const applySuggestion = (row) => saveTopic(row, row.match_suggested_topic);

  // All tier keys, ordered as shown in the popover.
  const ALL_TIERS = [
    { key: 'basic', label: 'Basic (free)' },
    { key: 'growth', label: 'Growth' },
    { key: 'premium', label: 'Premium' },
    { key: 'elite', label: 'Elite' },
  ];

  // A row without a `visible_to_tiers` field is treated as visible-to-all,
  // matching the backend's backward-compatible read path.
  const tiersForRow = (row) => {
    const t = row.visible_to_tiers;
    if (!Array.isArray(t)) return ALL_TIERS.map(x => x.key);
    return t;
  };

  const saveVisibility = async (row, nextTiers) => {
    setSavingVis(true);
    try {
      const r = await apiPatch(`/content/${row.content_id}/visibility`, {
        visible_to_tiers: nextTiers,
      });
      setItems(prev => prev.map(x => x.content_id === row.content_id
        ? { ...x, visible_to_tiers: r.visible_to_tiers }
        : x));
      toast.success(`Visibility updated · ${nextTiers.length || 'no'} tier${nextTiers.length === 1 ? '' : 's'}`);
    } catch (e) {
      toast.error(e?.message || 'Failed to update visibility');
    }
    setSavingVis(false);
  };

  // Pagination — hard cap of 250 per page (backend also enforces).
  const [pageSize, setPageSize] = useState(250);
  const [page, setPage] = useState(0);

  const inFlightRef = useRef(null);

  const load = useCallback(async () => {
    // Dedupe concurrent calls (prevents the dual-effect race that occasionally
    // surfaced a fake "Failed to load" toast even when data did populate) +
    // retry-with-backoff on transient failures + show the REAL error code in
    // the toast so debugging recurring failures is one screenshot away.
    if (inFlightRef.current) return inFlightRef.current;
    setLoading(true);

    const params = new URLSearchParams();
    if (typeTab !== 'all') params.append('content_type', typeTab);
    if (boardFilter) params.append('board', boardFilter);
    if (classFilter) params.append('class_level', classFilter);
    if (subjectFilter) params.append('subject', subjectFilter);
    if (q) params.append('q', q);
    // Server-side status filter — the admin must be able to paginate
    // through ALL rows of a single status, not just whatever shows up on
    // the current page after pagination.
    if (statusTab && statusTab !== 'all') params.append('status', statusTab);
    params.append('limit', String(pageSize));
    params.append('skip', String(page * pageSize));
    const url = `/content/admin-library?${params.toString()}`;

    const run = (async () => {
      let lastErr;
      for (let attempt = 0; attempt < 3; attempt++) {
        if (attempt > 0) await new Promise(res => setTimeout(res, attempt === 1 ? 700 : 1500));
        try {
          const r = await apiGet(url);
          setItems(r.items || []);
          setTotal(r.total || 0);
          if (r.status_counts) setStatusCounts(r.status_counts);
          setLoading(false);
          return r;
        } catch (e) {
          lastErr = e;
          if (e?.status === 401 || e?.status === 403) break;
        }
      }
      setLoading(false);
      const code = lastErr?.status ? `HTTP ${lastErr.status}` : (lastErr?.isNetworkError ? 'network error' : (lastErr?.message || 'unknown error'));
      toast.error(`Failed to load content library (${code})`, {
        action: { label: 'Retry', onClick: () => load() },
        duration: 8000,
      });
      throw lastErr;
    })().finally(() => { inFlightRef.current = null; });

    inFlightRef.current = run;
    return run.catch(() => {});
  }, [typeTab, boardFilter, classFilter, subjectFilter, q, statusTab, pageSize, page]);

  useEffect(() => { load(); }, [load, refreshToken]);

  // Reset to page 0 when filters / status tab / page size change.
  useEffect(() => { setPage(0); }, [typeTab, boardFilter, classFilter, subjectFilter, q, statusTab, pageSize]);

  // Match-state filter — set by clicking one of the summary chips.
  // Values: null (show all) | 'matched' | 'ambiguous' | 'orphaned'.
  const [matchFilter, setMatchFilter] = useState(null);

  // Status tab now filters server-side — apply the client-side match-state
  // filter on top when one is active. Reset to null clears the filter.
  const filtered = useMemo(() => {
    if (!matchFilter) return items;
    return items.filter(it => it.match_state === matchFilter);
  }, [items, matchFilter]);

  // Per-status totals from the backend (across all pages, not just the
  // current page). Keeps tab badges accurate even with 100s of rows.
  const counts = statusCounts;

  const subjectOptions = useMemo(
    () => Array.from(new Set(items.map(i => i.subject).filter(Boolean))).sort(),
    [items],
  );

  const approve = async (row) => {
    if (!window.confirm(`Approve "${row.title || prettyName(row.original_filename)}"?\n\nStudents will immediately start seeing this ${row.content_type} for ${row.board?.toUpperCase() || '—'} · Class ${row.class_level || '—'} · ${row.subject || '—'}${row.topic ? ' · ' + row.topic : ''}.`)) return;
    setMutatingId(row.content_id);
    try {
      await apiPost(`/content/${row.content_id}/approve`, {});
      toast.success('Approved — students will now see this content');
      setItems(prev => prev.map(x => x.content_id === row.content_id ? { ...x, status: 'approved' } : x));
      // Refresh per-status counts so the tab badges update without a
      // browser reload. The optimistic row patch above keeps the row's
      // status badge in sync immediately.
      load();
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : 'Approve failed');
    }
    setMutatingId(null);
  };

  const reject = async (row) => {
    if (!window.confirm(`Reject "${row.title || prettyName(row.original_filename)}"?\n\nThe submission + file stay in the library for audit, but students won't see it.`)) return;
    setMutatingId(row.content_id);
    try {
      await apiPost(`/content/${row.content_id}/reject`, {});
      toast.success('Rejected — hidden from students');
      setItems(prev => prev.map(x => x.content_id === row.content_id ? { ...x, status: 'rejected' } : x));
      load();
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : 'Reject failed');
    }
    setMutatingId(null);
  };

  const hardDelete = async (row) => {
    if (!window.confirm(`Permanently delete "${row.title || prettyName(row.original_filename)}"?\n\nThis removes the content_library row AND the file on disk (if not used elsewhere). Irreversible.`)) return;
    setMutatingId(row.content_id);
    try {
      await apiDelete(`/content/${row.content_id}?delete_file=true`);
      toast.success('Deleted');
      setItems(prev => prev.filter(x => x.content_id !== row.content_id));
      setTotal(t => Math.max(0, t - 1));
      load();
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : 'Delete failed');
    }
    setMutatingId(null);
  };

  // ----- BULK ACTIONS (same UX as Syllabus Library) -----
  const toggleRow = (cid) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(cid)) next.delete(cid); else next.add(cid);
      return next;
    });
  };
  const selectAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      filtered.forEach(r => next.add(r.content_id));
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const allVisibleSelected = filtered.length > 0 && filtered.every(r => selectedIds.has(r.content_id));
  const toggleSelectAllVisible = () => (allVisibleSelected ? clearSelection() : selectAllVisible());

  const runBulkAction = async (action) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) { toast.error('Nothing selected'); return; }
    const verbs = { approve: 'Approve', reject: 'Reject', delete: 'Permanently delete' };
    const verb = verbs[action];
    let msg = `${verb} ${ids.length} selected item${ids.length === 1 ? '' : 's'}?`;
    if (action === 'delete') {
      msg += '\n\nHARD DELETE — removes content rows AND files on disk. Irreversible.';
    } else if (action === 'approve') {
      msg += '\n\nStudents will immediately start seeing these.';
    } else {
      msg += '\n\nStudents will stop seeing these. Records stay for audit.';
    }
    if (!window.confirm(msg)) return;

    setBulkBusy(true);
    try {
      const r = await apiPost('/content/bulk-action', {
        content_ids: ids, action, delete_file: true,
      });
      const okCount = r.succeeded_count || 0;
      const failCount = r.failed_count || 0;
      const okSet = new Set(r.succeeded || []);
      if (action === 'delete') {
        setItems(prev => prev.filter(x => !okSet.has(x.content_id)));
        setTotal(t => Math.max(0, t - okCount));
      } else {
        // Phase 2.66 changed the backend canonical "approved" status from
        // the legacy "active". Bulk-approve had been writing "active" here
        // optimistically — which the row-badge renderer maps to its
        // unknown-status fallback (`pending_review`), so approved items
        // kept showing the yellow "PENDING REVIEW" badge until the user
        // hard-refreshed the browser. Use "approved" to match the server.
        const newStatus = action === 'approve' ? 'approved' : 'rejected';
        setItems(prev => prev.map(x => okSet.has(x.content_id) ? { ...x, status: newStatus } : x));
      }
      clearSelection();
      // Authoritative refresh — pulls the new per-status `status_counts`
      // from the server so the tab badges (Pending review / Approved /
      // Rejected / All) update immediately without a manual page reload.
      // The optimistic row update above keeps the table snappy in the
      // meantime; this just reconciles counts + drops items that no
      // longer match the current status tab.
      load();
      if (failCount === 0) toast.success(`${verb}d ${okCount} item${okCount === 1 ? '' : 's'}`);
      else if (okCount === 0) toast.error(`All ${failCount} failed`);
      else toast.warning(`${verb}d ${okCount}/${okCount + failCount} — ${failCount} failed`);
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : `${verb} failed`);
    }
    setBulkBusy(false);
  };

  const wipeAll = async () => {
    const scope = typeTab === 'all' ? 'ALL infographics AND cinegraphics' :
                  (typeTab === 'infographic' ? 'ALL infographics' : 'ALL cinegraphics');
    const phrase = window.prompt(
      `WIPE ${scope}, including files on disk?\n\nThis is irreversible. Type WIPE to confirm.`,
    );
    if (phrase !== 'WIPE') {
      if (phrase !== null) toast.error('Phrase did not match — cancelled.');
      return;
    }
    setWiping(true);
    try {
      const typeParam = typeTab !== 'all' ? `&content_type=${typeTab}` : '';
      const r = await apiPost(`/content/wipe-all?confirm=YES_WIPE_EVERYTHING${typeParam}`, {});
      toast.success(`Wiped: ${r.rows_deleted} rows · ${r.files_deleted} files`);
      setItems([]); setTotal(0); clearSelection();
      // Reset per-status counts so the tab badges drop to zero immediately
      // (and reconcile with the server, which now has nothing to count).
      load();
    } catch (e) {
      toast.error(e?.message === 'UNAUTHORIZED' ? 'Session expired — re-login' : 'Wipe failed');
    }
    setWiping(false);
  };

  return (
    <div className="space-y-4" data-testid="cm-content-library">
      <div className="flex items-start gap-2">
        <ImageIcon className="w-5 h-5 text-indigo-500 mt-0.5" />
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-800">Content Library (Infographics & Cinegraphics)</h3>
          <p className="text-[11px] text-slate-500 font-body mt-0.5">
            Every infographic or cinegraphic uploaded by admin/support-staff. Only <strong>approved</strong> items are visible to students.
          </p>
        </div>
        <Button onClick={wipeAll} disabled={wiping || items.length === 0}
          variant="ghost"
          className="ml-auto text-xs font-heading text-rose-700 hover:bg-rose-50 disabled:opacity-40"
          data-testid="cm-cl-wipe-all"
          title="Permanently delete EVERY row in current type filter, including files on disk. Irreversible.">
          <AlertOctagon className={`w-3.5 h-3.5 mr-1.5 ${wiping ? 'animate-pulse' : ''}`} />
          {wiping ? 'Wiping…' : `Wipe ${typeTab === 'all' ? 'all' : typeTab + 's'}`}
        </Button>
        <Button onClick={load} disabled={loading} variant="ghost"
          className="text-xs font-heading text-slate-600"
          data-testid="cm-cl-refresh">
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Type tabs */}
      <div className="flex flex-wrap gap-2">
        {TYPE_TABS.map(t => {
          const active = typeTab === t.key;
          return (
            <button key={t.key} onClick={() => setTypeTab(t.key)}
              data-testid={`cm-cl-type-${t.key}`}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-heading transition-all ${active ? 'bg-indigo-500 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}`}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Status tabs */}
      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map(t => {
          // 'all' = sum across statuses; status keys come from the server.
          const allCount = (counts.pending_review || 0) + (counts.approved || 0) + (counts.rejected || 0);
          const n = t.key === 'all' ? allCount : (counts[t.key] || 0);
          const active = statusTab === t.key;
          return (
            <button key={t.key} onClick={() => setStatusTab(t.key)}
              data-testid={`cm-cl-tab-${t.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-heading transition-all ${active ? 'bg-slate-900 text-white' : 'bg-white border border-slate-200 text-slate-600 hover:border-slate-300'}`}>
              {t.label}
              <span className={`ml-1.5 inline-block min-w-[20px] text-center text-[10px] rounded-full px-1.5 py-0.5 ${active ? 'bg-white/20' : 'bg-slate-100'}`}>
                {n}
              </span>
            </button>
          );
        })}
      </div>

      {/* Filters */}
      <Card className="p-3 rounded-2xl border-slate-100">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search title, chapter, filename…"
              className="pl-8 text-xs h-9 rounded-full border-slate-200"
              data-testid="cm-cl-search" />
          </div>
          <select value={boardFilter} onChange={e => setBoardFilter(e.target.value)}
            data-testid="cm-cl-board"
            className="text-xs rounded-full border border-slate-200 bg-white px-3 h-9 font-heading">
            {BOARD_FILTER.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
          </select>
          <select value={classFilter} onChange={e => setClassFilter(e.target.value)}
            data-testid="cm-cl-class"
            className="text-xs rounded-full border border-slate-200 bg-white px-3 h-9 font-heading">
            {CLASS_FILTER.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          {subjectOptions.length > 0 && (
            <select value={subjectFilter} onChange={e => setSubjectFilter(e.target.value)}
              data-testid="cm-cl-subject"
              className="text-xs rounded-full border border-slate-200 bg-white px-3 h-9 font-heading">
              <option value="">All subjects</option>
              {subjectOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}
          {(q || boardFilter || classFilter || subjectFilter) && (
            <button onClick={() => { setQ(''); setBoardFilter(''); setClassFilter(''); setSubjectFilter(''); }}
              className="text-[11px] text-slate-500 hover:text-slate-800 underline"
              data-testid="cm-cl-clear">Clear</button>
          )}
        </div>
      </Card>

      {/* Match-state summary bar — huge visual quick-scan for the admin
          after a bulk upload. Chips are CLICKABLE — clicking one filters
          the table to only that state so a "combined edit" pass can happen
          without hunting through pages. Click the active chip (or "Clear")
          to reset. */}
      {matchStats.total > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-2xl bg-gradient-to-r from-slate-50 to-white border border-slate-100"
          data-testid="cm-cl-match-summary">
          <span className="text-[11px] font-heading uppercase tracking-wider text-slate-500 mr-1">Syllabus match</span>
          <MatchChip icon={CheckCircle2} label="Matched" count={matchStats.matched} tone="emerald"
            active={matchFilter === 'matched'}
            onClick={() => setMatchFilter(matchFilter === 'matched' ? null : 'matched')}
            testid="cm-cl-match-matched" />
          <MatchChip icon={AlertTriangle} label="Ambiguous" count={matchStats.ambiguous} tone="amber"
            active={matchFilter === 'ambiguous'}
            onClick={() => setMatchFilter(matchFilter === 'ambiguous' ? null : 'ambiguous')}
            testid="cm-cl-match-ambiguous" />
          <MatchChip icon={XCircle} label="Orphaned" count={matchStats.orphaned} tone="rose"
            active={matchFilter === 'orphaned'}
            onClick={() => setMatchFilter(matchFilter === 'orphaned' ? null : 'orphaned')}
            testid="cm-cl-match-orphaned" />
          {matchFilter && (
            <button onClick={() => setMatchFilter(null)}
              data-testid="cm-cl-match-clear"
              className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-heading text-slate-500 hover:text-slate-800 hover:bg-slate-100 border border-transparent hover:border-slate-200 transition">
              <XIcon className="w-3 h-3" /> Clear filter
            </button>
          )}
          <span className="text-[10px] text-slate-400 ml-auto">
            {matchFilter
              ? `Showing only ${matchFilter} · ${filtered.length} of ${matchStats.total}`
              : matchStats.orphaned > 0 || matchStats.ambiguous > 0
                ? 'Click a chip to isolate that group · pencil per row to rename & retry.'
                : 'All items on this page are correctly linked to their syllabus chapters.'}
          </span>
        </div>
      )}

      {/* Bulk action bar — appears only when rows are selected */}
      {selectedIds.size > 0 && (
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 rounded-2xl bg-indigo-50 border border-indigo-100"
          data-testid="cm-cl-bulk-bar">
          <span className="text-xs font-heading text-indigo-800">
            {selectedIds.size} selected
          </span>
          <button onClick={clearSelection} disabled={bulkBusy}
            className="text-[11px] text-slate-500 hover:text-slate-800 underline disabled:opacity-40"
            data-testid="cm-cl-bulk-clear">
            Clear
          </button>
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            <Button onClick={() => runBulkAction('approve')} disabled={bulkBusy}
              data-testid="cm-cl-bulk-approve"
              className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full text-xs font-heading disabled:opacity-40">
              <ShieldCheck className="w-3.5 h-3.5 mr-1.5" /> Approve {selectedIds.size}
            </Button>
            <Button onClick={() => runBulkAction('reject')} disabled={bulkBusy}
              data-testid="cm-cl-bulk-reject"
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs font-heading disabled:opacity-40">
              <ShieldX className="w-3.5 h-3.5 mr-1.5" /> Reject {selectedIds.size}
            </Button>
            <Button onClick={() => runBulkAction('delete')} disabled={bulkBusy}
              data-testid="cm-cl-bulk-delete"
              className="bg-rose-500 hover:bg-rose-600 text-white rounded-full text-xs font-heading disabled:opacity-40">
              <Trash2 className="w-3.5 h-3.5 mr-1.5" /> Delete {selectedIds.size}
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <Card className="p-0 rounded-2xl border-slate-100 overflow-hidden">
        {loading && items.length === 0 ? (
          <div className="py-16 flex items-center justify-center"><RefreshCw className="w-5 h-5 text-indigo-500 animate-spin" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <FileText className="w-9 h-9 mx-auto text-slate-300 mb-2" />
            <p className="text-xs text-slate-500 font-body">
              {statusTab === 'pending_review'
                ? 'Nothing pending review. Upload infographics from "Bulk Infographics", or a cinegraphic from "Upload Content".'
                : 'No items match these filters.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs table-fixed">
              <colgroup>
                <col className="w-[3%]" />
                <col className="w-[6%]" />
                <col className="w-[20%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[8%]" />
                <col className="w-[16%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
              </colgroup>
              <thead className="bg-slate-50/70 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-center px-2 py-2">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      onChange={toggleSelectAllVisible}
                      disabled={bulkBusy || filtered.length === 0}
                      data-testid="cm-cl-select-all"
                      className="rounded border-slate-300 cursor-pointer"
                      title={allVisibleSelected ? 'Clear selection' : 'Select all visible rows'}
                    />
                  </th>
                  <th className="text-center px-3 py-2">Preview</th>
                  <th className="text-left px-3 py-2">File</th>
                  <th className="text-left px-3 py-2">Scope</th>
                  <th className="text-left px-3 py-2">Chapter</th>
                  <th className="text-center px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Visible to tiers</th>
                  <th className="text-center px-3 py-2">Uploaded</th>
                  <th className="text-center px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const st = r.status || 'pending_review';
                  const busy = mutatingId === r.content_id;
                  const fileHref = r.on_disk ? `${FILE_ORIGIN}${r.file_url}` : null;
                  return (
                    <tr key={r.content_id}
                      className={`border-t border-slate-100 hover:bg-slate-50/40 align-middle ${selectedIds.has(r.content_id) ? 'bg-indigo-50/40' : ''}`}>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(r.content_id)}
                          onChange={() => toggleRow(r.content_id)}
                          disabled={bulkBusy || busy}
                          data-testid={`cm-cl-row-select-${i}`}
                          className="rounded border-slate-300 cursor-pointer"
                        />
                      </td>
                      <td className="px-2 py-2 text-center">
                        {r.content_type === 'infographic' && fileHref ? (
                          <a href={fileHref} target="_blank" rel="noreferrer">
                            <img src={fileHref} alt="" loading="lazy"
                              className="w-14 h-10 object-cover rounded border border-slate-200 mx-auto hover:ring-2 hover:ring-indigo-300" />
                          </a>
                        ) : r.content_type === 'cinegraphic' ? (
                          <div className="w-14 h-10 flex items-center justify-center rounded bg-slate-100 mx-auto">
                            <Video className="w-4 h-4 text-slate-500" />
                          </div>
                        ) : (
                          <div className="w-14 h-10 flex items-center justify-center rounded bg-slate-100 mx-auto">
                            <FileText className="w-4 h-4 text-slate-400" />
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="flex items-start gap-2 min-w-0">
                          <div className="min-w-0 flex-1">
                            <p className="font-body text-slate-800 truncate leading-snug" title={r.title || prettyName(r.original_filename)}>
                              {r.title || prettyName(r.original_filename) || '—'}
                            </p>
                            <p className="text-[10px] text-slate-400 font-body truncate mt-0.5" title={prettyName(r.original_filename)}>
                              {prettyName(r.original_filename) || r.content_id}
                            </p>
                            <p className="text-[10px] text-slate-400 font-body mt-0.5">
                              {r.size_mb ? `${r.size_mb.toFixed(1)} MB` : '—'}
                              {!r.on_disk && <span className="text-rose-500 ml-1 inline-flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" />missing file</span>}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2.5">
                        <p className="font-body text-slate-700 leading-snug truncate">
                          <span className="font-heading text-indigo-600 uppercase tracking-wide text-[10px]">{r.board || '—'}</span>
                          <span className="text-slate-300 mx-1.5">·</span>
                          <span>Class {r.class_level || '—'}</span>
                        </p>
                        <p className="text-[10px] text-emerald-700 font-body truncate mt-0.5">{r.subject || '—'}</p>
                        <Badge className={`mt-1 rounded-full text-[9px] uppercase tracking-wider ${r.content_type === 'cinegraphic' ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'}`}>
                          {r.content_type}
                        </Badge>
                      </td>
                      <td className="px-3 py-2.5 font-body text-slate-700">
                        {editingTopicId === r.content_id ? (
                          <div className="flex items-center gap-1" data-testid={`cm-cl-topic-edit-${i}`}>
                            <input
                              type="text"
                              value={editingTopicValue}
                              onChange={e => setEditingTopicValue(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter') saveTopic(r, editingTopicValue);
                                else if (e.key === 'Escape') cancelEditTopic();
                              }}
                              disabled={savingTopic}
                              autoFocus
                              className="w-full min-w-[140px] px-2 py-1 rounded border border-indigo-300 focus:border-indigo-500 focus:outline-none text-xs bg-white"
                              data-testid={`cm-cl-topic-input-${i}`}
                              placeholder="Chapter name…"
                            />
                            <button onClick={() => saveTopic(r, editingTopicValue)} disabled={savingTopic}
                              className="p-1 rounded hover:bg-emerald-50 text-emerald-600 disabled:opacity-40"
                              title="Save (Enter)"
                              data-testid={`cm-cl-topic-save-${i}`}>
                              <CheckIcon className="w-3.5 h-3.5" />
                            </button>
                            <button onClick={cancelEditTopic} disabled={savingTopic}
                              className="p-1 rounded hover:bg-rose-50 text-rose-500 disabled:opacity-40"
                              title="Cancel (Esc)"
                              data-testid={`cm-cl-topic-cancel-${i}`}>
                              <XIcon className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-1.5 min-w-0">
                              <MatchBadge row={r} testIdSuffix={i} />
                              <p className="truncate flex-1" title={r.topic}>{r.topic || <span className="text-slate-400 italic">—</span>}</p>
                              <button onClick={() => startEditTopic(r)}
                                className="p-0.5 rounded text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 shrink-0"
                                title="Edit chapter name (retry match)"
                                data-testid={`cm-cl-topic-edit-btn-${i}`}>
                                <Pencil className="w-3 h-3" />
                              </button>
                            </div>
                            {r.heading && <p className="text-[10px] text-slate-400 truncate mt-0.5" title={r.heading}>{r.heading}</p>}
                            {r.match_state === 'orphaned' && r.match_suggested_topic && (
                              <button
                                onClick={() => applySuggestion(r)}
                                disabled={savingTopic}
                                data-testid={`cm-cl-apply-suggestion-${i}`}
                                title={`Suggested: ${r.match_suggested_topic}`}
                                className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 hover:bg-amber-100 border border-amber-200 text-[10px] font-heading text-amber-800 max-w-full disabled:opacity-40">
                                <Zap className="w-2.5 h-2.5 shrink-0" />
                                <span className="truncate">Apply · {r.match_suggested_topic}</span>
                              </button>
                            )}
                            {r.match_state === 'ambiguous' && r.match_matched_chapters?.length > 1 && (
                              <p className="text-[10px] text-amber-700 mt-0.5 truncate" title={r.match_matched_chapters.join(' · ')}>
                                Matches {r.match_matched_chapters.length} chapters — rename to disambiguate
                              </p>
                            )}
                          </>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-center">{statusBadge(st)}</td>
                      <td className="px-3 py-2.5">
                        {(() => {
                          const tiers = tiersForRow(r);
                          const isAll = tiers.length === ALL_TIERS.length;
                          const isEditing = visEditingId === r.content_id;
                          return (
                            <div className="relative">
                              <button
                                onClick={() => setVisEditingId(isEditing ? null : r.content_id)}
                                disabled={savingVis}
                                data-testid={`cm-cl-vis-toggle-${i}`}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-50 border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50/40 text-[10px] font-heading text-slate-600 truncate max-w-full"
                                title="Click to choose which subscription tiers see this content"
                              >
                                <Eye className="w-3 h-3 text-slate-400 shrink-0" />
                                {isAll
                                  ? <span className="text-emerald-700">All tiers</span>
                                  : tiers.length === 0
                                    ? <span className="text-rose-600">Hidden</span>
                                    : <span className="truncate">{tiers.map(t => t.replace('_', ' ').replace('basic lifetime', 'B.L.')).join(', ')}</span>}
                              </button>
                              {isEditing && (
                                <div
                                  className="absolute z-20 mt-1 left-0 w-56 bg-white rounded-xl shadow-lg border border-slate-200 p-3"
                                  data-testid={`cm-cl-vis-popover-${i}`}
                                >
                                  <p className="text-[10px] font-heading uppercase tracking-wider text-slate-500 mb-2">Visible to subscription tiers</p>
                                  <div className="space-y-1.5">
                                    {ALL_TIERS.map(t => {
                                      const checked = tiers.includes(t.key);
                                      return (
                                        <label key={t.key}
                                          className="flex items-center gap-2 text-[11px] font-body text-slate-700 cursor-pointer hover:text-slate-900">
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            disabled={savingVis}
                                            data-testid={`cm-cl-vis-${t.key}-${i}`}
                                            onChange={() => {
                                              const next = checked
                                                ? tiers.filter(x => x !== t.key)
                                                : [...tiers, t.key];
                                              saveVisibility(r, next);
                                            }}
                                            className="rounded border-slate-300 cursor-pointer"
                                          />
                                          <span>{t.label}</span>
                                        </label>
                                      );
                                    })}
                                  </div>
                                  <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
                                    <button
                                      onClick={() => saveVisibility(r, ALL_TIERS.map(t => t.key))}
                                      disabled={savingVis}
                                      className="text-[10px] font-heading text-indigo-600 hover:underline">All</button>
                                    <button
                                      onClick={() => saveVisibility(r, ['growth', 'premium', 'elite'])}
                                      disabled={savingVis}
                                      className="text-[10px] font-heading text-slate-500 hover:underline"
                                      title="Hide from Basic + Basic Lifetime tiers">Paid only</button>
                                    <button
                                      onClick={() => setVisEditingId(null)}
                                      className="text-[10px] font-heading text-slate-400 hover:text-slate-600">Close</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-3 py-2.5 text-center text-[10px] text-slate-500 font-body whitespace-nowrap">
                        {formatUploadedDate(r.created_at)}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <div className="flex items-center justify-center gap-1.5 whitespace-nowrap">
                          {fileHref && (
                            <a href={fileHref} target="_blank" rel="noreferrer"
                              className="p-1.5 rounded-md text-indigo-500 hover:bg-indigo-50 hover:text-indigo-700"
                              title="Open file">
                              <ExternalLink className="w-3.5 h-3.5" />
                            </a>
                          )}
                          {st !== 'approved' && (
                            <button onClick={() => approve(r)} disabled={busy}
                              data-testid={`cm-cl-approve-${i}`}
                              className="p-1.5 rounded-md text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"
                              title="Approve — students see this">
                              {busy ? <span className="text-[10px]">…</span> : <ShieldCheck className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          {st !== 'rejected' && (
                            <button onClick={() => reject(r)} disabled={busy}
                              data-testid={`cm-cl-reject-${i}`}
                              className="p-1.5 rounded-md text-amber-600 hover:bg-amber-50 disabled:opacity-40"
                              title="Reject — hidden from students">
                              {busy ? <span className="text-[10px]">…</span> : <ShieldX className="w-3.5 h-3.5" />}
                            </button>
                          )}
                          <button onClick={() => hardDelete(r)} disabled={busy}
                            data-testid={`cm-cl-delete-${i}`}
                            className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50 disabled:opacity-40"
                            title="Permanently delete">
                            {busy ? <span className="text-[10px]">…</span> : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        <div className="px-4 py-2 bg-slate-50/60 border-t border-slate-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500 font-body">
          <span className="inline-flex items-center gap-1"><Info className="w-3 h-3" />
            {(() => {
              const startRow = total === 0 ? 0 : page * pageSize + 1;
              const endRow = Math.min(total, (page + 1) * pageSize);
              return (
                <span data-testid="cm-cl-pagination-summary">
                  Showing rows <strong>{startRow}–{endRow}</strong> of <strong>{total}</strong>
                  {filtered.length !== items.length && (
                    <span className="text-slate-400"> · {filtered.length} match current tab</span>
                  )}
                </span>
              );
            })()}
          </span>
          {/* Pagination — hard-capped at 250/page server-side. */}
          <div className="inline-flex items-center gap-2 ml-auto" data-testid="cm-cl-pagination">
            <label className="inline-flex items-center gap-1 text-slate-500">
              Per page
              <select
                value={pageSize}
                onChange={e => setPageSize(parseInt(e.target.value, 10) || 250)}
                data-testid="cm-cl-page-size"
                disabled={loading}
                className="border border-slate-200 rounded-md px-1.5 py-0.5 text-[10px] bg-white hover:border-indigo-300 focus:outline-none focus:ring-1 focus:ring-indigo-300">
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="250">250</option>
              </select>
            </label>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0 || loading}
              data-testid="cm-cl-prev-page"
              className="px-2 py-0.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 disabled:opacity-30 disabled:cursor-not-allowed">
              ← Prev
            </button>
            <span className="text-slate-500">
              Page <strong>{page + 1}</strong> of <strong>{Math.max(1, Math.ceil(total / pageSize))}</strong>
            </span>
            <button
              onClick={() => setPage(p => p + 1)}
              disabled={(page + 1) * pageSize >= total || loading}
              data-testid="cm-cl-next-page"
              className="px-2 py-0.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:border-indigo-300 disabled:opacity-30 disabled:cursor-not-allowed">
              Next →
            </button>
          </div>
        </div>
        <div className="px-4 py-2 bg-slate-50/60 border-t border-slate-100 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-slate-500 font-body">
          <span className="inline-flex items-center gap-1"><ExternalLink className="w-3 h-3 text-indigo-500" /> View</span>
          <span className="inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-600" /> Approve</span>
          <span className="inline-flex items-center gap-1"><ShieldX className="w-3 h-3 text-amber-600" /> Reject</span>
          <span className="inline-flex items-center gap-1"><Trash2 className="w-3 h-3 text-rose-500" /> Delete</span>
          <span className="text-slate-300">|</span>
          <span className="text-emerald-700">Approved items are live for students immediately.</span>
        </div>
      </Card>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Match state visual affordances (Feb 2026)
// Small icon-first badges that let an admin scanning a page of 250 rows see
// at a glance which uploads are correctly linked to a syllabus chapter and
// which need attention. Kept intentionally chunky (18px icons + 10px count
// pill) so orphaned rows visually "pop" against a sea of green checks.
// ---------------------------------------------------------------------------
function MatchBadge({ row, testIdSuffix }) {
  const state = row.match_state;
  if (!state) return null; // legacy rows before backend enrichment
  const cfg =
    state === 'matched'
      ? {
          Icon: CheckCircle2,
          tone: 'text-emerald-600',
          bg: 'bg-emerald-50 border-emerald-200',
          title: `Matched — students see this asset under "${row.match_matched_chapters?.[0] || row.topic}"`,
          testid: `cm-cl-badge-matched-${testIdSuffix}`,
        }
      : state === 'ambiguous'
      ? {
          Icon: AlertTriangle,
          tone: 'text-amber-600',
          bg: 'bg-amber-50 border-amber-200',
          title: `Ambiguous — matches ${row.match_matched_chapters?.length || 'multiple'} chapters: ${(row.match_matched_chapters || []).join(' · ')}`,
          testid: `cm-cl-badge-ambiguous-${testIdSuffix}`,
        }
      : {
          Icon: XCircle,
          tone: 'text-rose-600',
          bg: 'bg-rose-50 border-rose-200',
          title: row.match_suggested_topic
            ? `Orphaned — no chapter matches. Try: "${row.match_suggested_topic}"`
            : 'Orphaned — no matching syllabus chapter. Rename the chapter to link it.',
          testid: `cm-cl-badge-orphaned-${testIdSuffix}`,
        };
  return (
    <span
      title={cfg.title}
      data-testid={cfg.testid}
      className={`inline-flex items-center justify-center w-5 h-5 rounded-full border ${cfg.bg} ${cfg.tone} shrink-0`}
    >
      <cfg.Icon className="w-3 h-3" />
    </span>
  );
}

function MatchChip({ icon: Icon, label, count, tone, testid, onClick, active = false }) {
  const map = {
    emerald: {
      base: 'bg-emerald-50 border-emerald-200 text-emerald-700',
      active: 'bg-emerald-600 border-emerald-700 text-white shadow-sm',
      hover: 'hover:bg-emerald-100',
    },
    amber: {
      base: 'bg-amber-50 border-amber-200 text-amber-800',
      active: 'bg-amber-500 border-amber-600 text-white shadow-sm',
      hover: 'hover:bg-amber-100',
    },
    rose: {
      base: 'bg-rose-50 border-rose-200 text-rose-700',
      active: 'bg-rose-600 border-rose-700 text-white shadow-sm',
      hover: 'hover:bg-rose-100',
    },
  };
  const cfg = map[tone] || map.emerald;
  const isButton = typeof onClick === 'function';
  const cls = `inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-heading transition ${
    active ? cfg.active : `${cfg.base} ${isButton ? cfg.hover + ' cursor-pointer' : ''}`
  } ${count === 0 && !active ? 'opacity-50' : ''}`;
  const pillCls = `inline-flex items-center justify-center min-w-[18px] h-4 px-1 rounded-full text-[10px] font-bold ${
    active ? 'bg-white/25 text-white' : 'bg-white/70'
  }`;
  const content = (
    <>
      <Icon className="w-3.5 h-3.5" />
      <span>{label}</span>
      <span className={pillCls}>{count}</span>
      {active && <CheckIcon className="w-3 h-3 ml-0.5" />}
    </>
  );
  return isButton ? (
    <button
      onClick={onClick}
      data-testid={testid}
      aria-pressed={active}
      title={active ? `Click to clear ${label.toLowerCase()} filter` : `Filter to ${label.toLowerCase()} only`}
      className={cls}
    >
      {content}
    </button>
  ) : (
    <span data-testid={testid} className={cls}>{content}</span>
  );
}
